import { Box3 } from 'three';
import type { Ray } from 'three';

/**
 * 对稳定整数身份建立 AABB 二叉树，并仅 refit 变化叶及其祖先。
 *
 * 树节点与边界由索引拥有，查询栈和输出缓冲复用；对象、几何及业务身份由调用方管理。
 * null/非有限边界保守透传，空边界明确不参与查询，避免把不可靠边界当作未命中。
 */
export class AabbRayIndex {
  /** 小叶子直接测试盒，避免小实例组承担不必要的深树成本。 */
  private static readonly LEAF_CAPACITY = 8;
  /** 每个稳定身份的自有边界；null 表示不可可靠过滤。 */
  private readonly bounds: Array<Box3 | null> = [];
  /** 有可靠且非空边界的身份排序表。 */
  private readonly order: number[] = [];
  /** 保守透传身份。 */
  private readonly unbounded: number[] = [];
  /** 先父后子的扁平树，便于局部 refit 按下标倒序结算。 */
  private readonly nodes: RaycastAabbNode[] = [];
  /** 身份到叶节点的直接索引。 */
  private leafByEntry = new Int32Array(0);
  /** 结构变化时延迟合并重建。 */
  private structureDirty = true;
  /** 本批需要重算的叶节点。 */
  private readonly dirtyLeaves = new Set<number>();
  /** 多个变化叶共用的祖先只重算一次。 */
  private readonly dirtyBranches = new Set<number>();
  /** 倒序结算祖先时复用的下标表。 */
  private readonly branchOrder: number[] = [];
  /** 查询使用的复用栈。 */
  private readonly queryStack: number[] = [];
  /** 累计结构重建次数。 */
  private rebuilds = 0;
  /** 累计 refit 叶节点数量。 */
  private refittedLeaves = 0;
  /** 累计空间中位分区比较次数。 */
  private buildPartitionComparisons = 0;
  /** 最近查询的盒测试数。 */
  private lastBoundsTests = 0;
  /** 最近查询的候选数。 */
  private lastCandidates = 0;
  /** 生命周期已结束。 */
  private disposed = false;

  /** @param count 当前结构中的稳定整数身份数量。 */
  public reset(count: number): void {
    if (this.disposed) throw new Error('AABB 射线索引已释放');
    if (!Number.isInteger(count) || count < 0) throw new Error('AABB 索引数量必须是非负整数');
    this.bounds.length = count;
    this.bounds.fill(null);
    this.leafByEntry = new Int32Array(count);
    this.leafByEntry.fill(-1);
    this.structureDirty = true;
    this.dirtyLeaves.clear();
  }

  /**
   * 写入一个身份的新包围盒；调用方传入的临时盒不会被保留。
   * @param id 当前结构内的稳定整数身份。
   * @param bounds 新边界，null 表示无法可靠粗筛。
   */
  public setBounds(id: number, bounds: Box3 | null): void {
    if (this.disposed) return;
    if (!Number.isInteger(id) || id < 0 || id >= this.bounds.length) throw new Error(`AABB 索引身份越界：${id}`);
    const previous = this.bounds[id];
    // 仅 Three.js 标准空盒允许无限值；损坏盒不能因另一轴倒置而被当成空盒丢弃。
    const canonicalEmpty = bounds && bounds.min.x === Infinity && bounds.min.y === Infinity
      && bounds.min.z === Infinity && bounds.max.x === -Infinity && bounds.max.y === -Infinity
      && bounds.max.z === -Infinity;
    const usable = bounds && (canonicalEmpty || (
      Number.isFinite(bounds.min.x) && Number.isFinite(bounds.min.y) && Number.isFinite(bounds.min.z)
      && Number.isFinite(bounds.max.x) && Number.isFinite(bounds.max.y) && Number.isFinite(bounds.max.z)
    ));
    const wasBounded = previous !== null && !previous.isEmpty();
    const isBounded = Boolean(usable && !bounds!.isEmpty());
    const wasUnbounded = previous === null;
    if (usable) {
      if (previous?.equals(bounds!)) return;
      this.bounds[id] = (previous ?? new Box3()).copy(bounds!);
    } else {
      this.bounds[id] = null;
    }
    if (wasBounded !== isBounded || wasUnbounded !== !usable) this.structureDirty = true;
    if (!this.structureDirty && isBounded) this.dirtyLeaves.add(this.leafByEntry[id]);
  }

  /** 提交结构或局部边界变化，不做射线求交。 */
  public flush(): void {
    if (this.disposed) return;
    if (this.structureDirty) {
      this.rebuild();
      return;
    }
    this.dirtyBranches.clear();
    for (const leafIndex of this.dirtyLeaves) {
      const node = this.nodes[leafIndex];
      this.refreshLeafBounds(node);
      this.refittedLeaves++;
      let parent = node.parent;
      while (parent !== -1 && !this.dirtyBranches.has(parent)) {
        this.dirtyBranches.add(parent);
        parent = this.nodes[parent].parent;
      }
    }
    this.branchOrder.length = 0;
    for (const id of this.dirtyBranches) this.branchOrder.push(id);
    this.branchOrder.sort(AabbRayIndex.compareDescending);
    for (const id of this.branchOrder) {
      const node = this.nodes[id];
      node.bounds.copy(this.nodes[node.left].bounds).union(this.nodes[node.right].bounds);
    }
    this.dirtyLeaves.clear();
    this.dirtyBranches.clear();
  }

  /**
   * 返回射线经过的身份；保持升序以保留调用方原始遍历的稳定 tie-break。
   * @param ray 与索引边界处于同一坐标空间的射线。
   * @param target 调用方复用的候选缓冲，本方法先清空再填充。
   */
  public query(ray: Ray, target: number[]): void {
    target.length = 0;
    this.lastBoundsTests = 0;
    this.lastCandidates = 0;
    if (this.disposed) return;
    this.flush();
    this.queryStack.length = 0;
    if (this.nodes.length > 0) this.queryStack.push(0);
    while (this.queryStack.length > 0) {
      const node = this.nodes[this.queryStack.pop()!];
      this.lastBoundsTests++;
      if (!ray.intersectsBox(node.bounds)) continue;
      if (node.left === -1) {
        for (let index = node.start; index < node.end; index++) {
          const id = this.order[index];
          this.lastBoundsTests++;
          if (ray.intersectsBox(this.bounds[id]!)) target.push(id);
        }
      } else {
        this.queryStack.push(node.right, node.left);
      }
    }
    for (const id of this.unbounded) target.push(id);
    target.sort(AabbRayIndex.compareAscending);
    this.lastCandidates = target.length;
  }

  /**
   * 复制已发布的单项边界，供射线以外的只读空间消费者复用。
   * 不暴露内部 Box3，也不以树根代替带可见性筛选的模型范围。
   * @param id 当前索引身份。
   * @param target 调用方持有的复用盒。
   * @returns 是否有可靠边界；空盒仍为可靠边界，不可靠或已释放时返回 false。
   */
  public copyBounds(id: number, target: Box3): boolean {
    target.makeEmpty();
    const bounds = this.disposed ? null : this.bounds[id];
    if (!bounds) return false;
    target.copy(bounds);
    return true;
  }

  /** @returns 显式读取的工作量快照，不在查询循环中创建统计对象。 */
  public getStats(): RaycastAabbIndexStats {
    return { entries: this.bounds.length, nodes: this.nodes.length, rebuilds: this.rebuilds,
      refittedLeaves: this.refittedLeaves, buildPartitionComparisons: this.buildPartitionComparisons,
      lastBoundsTests: this.lastBoundsTests, lastCandidates: this.lastCandidates };
  }

  /** 释放边界、节点和查询缓冲，不拥有任何业务对象或 GPU 资源。 */
  public dispose(): void {
    this.disposed = true;
    this.bounds.length = this.order.length = this.unbounded.length = this.nodes.length = 0;
    this.queryStack.length = this.branchOrder.length = 0;
    this.dirtyLeaves.clear();
    this.dirtyBranches.clear();
    this.leafByEntry = new Int32Array(0);
  }

  /** 结构变化时重建一次；无变化查询不重新发现或排序身份集合。 */
  private rebuild(): void {
    this.order.length = this.unbounded.length = this.nodes.length = 0;
    this.leafByEntry.fill(-1);
    for (let id = 0; id < this.bounds.length; id++) {
      const bounds = this.bounds[id];
      if (bounds === null) this.unbounded.push(id);
      else if (!bounds.isEmpty()) this.order.push(id);
    }
    if (this.order.length > 0) this.buildNode(0, this.order.length, -1);
    this.dirtyLeaves.clear();
    this.structureDirty = false;
    this.rebuilds++;
  }

  /** @param start 区间起点。 @param end 区间终点。 @param parent 父节点下标。 @returns 新节点下标。 */
  private buildNode(start: number, end: number, parent: number): number {
    const id = this.nodes.length;
    const node: RaycastAabbNode = { bounds: new Box3(), parent, left: -1, right: -1, start, end };
    this.nodes.push(node);
    this.refreshLeafBounds(node);
    if (end - start <= AabbRayIndex.LEAF_CAPACITY) {
      for (let index = start; index < end; index++) this.leafByEntry[this.order[index]] = id;
      return id;
    }
    const width = node.bounds.max.x - node.bounds.min.x;
    const height = node.bounds.max.y - node.bounds.min.y;
    const depth = node.bounds.max.z - node.bounds.min.z;
    const axis = width >= height && width >= depth ? 'x' : height >= depth ? 'y' : 'z';
    const middle = start + Math.floor((end - start) / 2);
    // 只确定本节点的空间中位位置；子范围由递归继续分区，避免每个分支复制并完整排序。
    this.partitionOrderAtMedian(start, end, middle, axis);
    node.left = this.buildNode(start, middle, id);
    node.right = this.buildNode(middle, end, id);
    return id;
  }

  /**
   * 在原始身份表中把目标中位元素分区到确定位置。
   *
   * 设计说明：
   * 1. 当前节点只需要左右两组，不需要完整排序整个范围；
   * 2. 原位 quickselect 不创建子数组，且继续以身份作为中心相同情况下的确定性次级键；
   * 3. 每个子节点仍会按自己的最大空间轴重新分区，保持原有 BVH 划分语义。
   *
   * @param start 范围起点。
   * @param end 范围终点，不包含该位置。
   * @param median 目标中位下标。
   * @param axis 当前节点最大空间轴。
   */
  private partitionOrderAtMedian(start: number, end: number, median: number, axis: 'x' | 'y' | 'z'): void {
    let left = start;
    let right = end - 1;
    while (left < right) {
      const pivotIndex = this.selectMedianPivotIndex(left, right, axis);
      const partitionIndex = this.partitionOrderRange(left, right, pivotIndex, axis);
      if (partitionIndex === median) return;
      if (partitionIndex < median) left = partitionIndex + 1;
      else right = partitionIndex - 1;
    }
  }

  /**
   * 选择首、中、尾三个候选的中值作为枢轴，降低有序与逆序输入的退化风险。
   *
   * @param left 当前左边界。
   * @param right 当前右边界。
   * @param axis 当前空间轴。
   * @returns 枢轴所在下标。
   */
  private selectMedianPivotIndex(left: number, right: number, axis: 'x' | 'y' | 'z'): number {
    const middle = left + Math.floor((right - left) / 2);
    const leftId = this.order[left];
    const middleId = this.order[middle];
    const rightId = this.order[right];
    if (this.compareOrderEntries(leftId, middleId, axis) <= 0) {
      if (this.compareOrderEntries(middleId, rightId, axis) <= 0) return middle;
      return this.compareOrderEntries(leftId, rightId, axis) <= 0 ? right : left;
    }
    if (this.compareOrderEntries(leftId, rightId, axis) <= 0) return left;
    return this.compareOrderEntries(middleId, rightId, axis) <= 0 ? right : middle;
  }

  /**
   * 使用 Lomuto 分区把枢轴放到最终位置，范围内交换均复用原身份数组。
   *
   * @param left 当前左边界。
   * @param right 当前右边界。
   * @param pivotIndex 枢轴下标。
   * @param axis 当前空间轴。
   * @returns 枢轴分区后的最终下标。
   */
  private partitionOrderRange(left: number, right: number, pivotIndex: number, axis: 'x' | 'y' | 'z'): number {
    const pivotId = this.order[pivotIndex];
    this.swapOrderEntries(pivotIndex, right);
    let storeIndex = left;
    for (let index = left; index < right; index++) {
      if (this.compareOrderEntries(this.order[index], pivotId, axis) >= 0) continue;
      this.swapOrderEntries(storeIndex, index);
      storeIndex++;
    }
    this.swapOrderEntries(storeIndex, right);
    return storeIndex;
  }

  /**
   * 比较两个空间身份在指定轴上的包围盒中心，中心相同时以身份保证确定性。
   *
   * @param leftId 左侧身份。
   * @param rightId 右侧身份。
   * @param axis 当前空间轴。
   * @returns 标准升序比较值。
   */
  private compareOrderEntries(leftId: number, rightId: number, axis: 'x' | 'y' | 'z'): number {
    this.buildPartitionComparisons++;
    const leftBounds = this.bounds[leftId]!;
    const rightBounds = this.bounds[rightId]!;
    return (leftBounds.min[axis] + leftBounds.max[axis])
      - (rightBounds.min[axis] + rightBounds.max[axis]) || leftId - rightId;
  }

  /**
   * 原位交换两个空间身份。
   *
   * @param left 左下标。
   * @param right 右下标。
   */
  private swapOrderEntries(left: number, right: number): void {
    if (left === right) return;
    const value = this.order[left];
    this.order[left] = this.order[right];
    this.order[right] = value;
  }

  /** @param node 只汇总其连续身份范围的叶节点。 */
  private refreshLeafBounds(node: RaycastAabbNode): void {
    node.bounds.makeEmpty();
    for (let index = node.start; index < node.end; index++) node.bounds.union(this.bounds[this.order[index]]!);
  }

  /** @param left 左下标。 @param right 右下标。 @returns 升序比较结果。 */
  private static compareAscending(left: number, right: number): number { return left - right; }
  /** @param left 左下标。 @param right 右下标。 @returns 倒序比较结果。 */
  private static compareDescending(left: number, right: number): number { return right - left; }
}
